import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Calculator as CalcIcon, ArrowRight, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import SEO from '../components/SEO';

const Calculator = () => {
  const [loanAmount, setLoanAmount] = useState(100000);
  const [interestRate, setInterestRate] = useState(7.5);
  const [loanTerm, setLoanTerm] = useState(60);
  const [assetType, setAssetType] = useState('equipment');
  const [results, setResults] = useState({
    monthlyPayment: 0,
    totalPayment: 0,
    totalInterest: 0
  });

  const calculateRepayment = useCallback(() => {
    const principal = loanAmount;
    const annualRate = interestRate / 100;
    const monthlyRate = annualRate / 12;
    const numPayments = loanTerm;

    let monthlyPayment;
    if (monthlyRate === 0) {
      monthlyPayment = principal / numPayments;
    } else {
      monthlyPayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1);
    }

    const totalPayment = monthlyPayment * numPayments;
    const totalInterest = totalPayment - principal;

    setResults({
      monthlyPayment: Math.round(monthlyPayment * 100) / 100,
      totalPayment: Math.round(totalPayment * 100) / 100,
      totalInterest: Math.round(totalInterest * 100) / 100
    });
  }, [loanAmount, interestRate, loanTerm]);

  useEffect(() => {
    calculateRepayment();
  }, [calculateRepayment]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatCurrencyFull = (value) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Calculate progress ring
  const principalPercent = (loanAmount / results.totalPayment) * 100 || 0;
  const interestPercent = (results.totalInterest / results.totalPayment) * 100 || 0;

  return (
    <div className="min-h-screen pt-20">
      <SEO 
        title="Finance Calculator | Estimate Your Loan Repayments"
        description="Use our free finance calculator to estimate your monthly loan repayments. Calculate equipment finance, vehicle finance, and business loan costs instantly. Get a personalised quote today."
        keywords="finance calculator Australia, loan repayment calculator, equipment finance calculator, business loan calculator, monthly repayment estimator, commercial finance calculator"
        canonical="/calculator"
      />
      {/* Hero Section */}
      <section className="py-16 lg:py-20 bg-navy-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold-500/10 border border-gold-500/30 rounded-sm mb-6">
              <CalcIcon size={16} className="text-gold-500" />
              <span className="text-gold-500 font-body text-sm font-medium">Instant Estimate</span>
            </div>
            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-white uppercase tracking-tight leading-none mb-4">
              Finance<br />
              <span className="gold-gradient-text">Calculator</span>
            </h1>
            <p className="font-body text-lg text-white/70">
              Estimate your monthly repayments in seconds. Adjust the values below to see your personalised quote.
            </p>
          </div>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-16 lg:py-24 bg-navy-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Inputs */}
            <div className="bg-navy-700 border border-white/5 p-8 lg:p-10">
              <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight mb-8">
                Loan Details
              </h2>

              <div className="space-y-8">
                {/* Asset Type */}
                <div className="space-y-3">
                  <Label className="font-body text-white/80">Asset Type</Label>
                  <Select value={assetType} onValueChange={setAssetType}>
                    <SelectTrigger 
                      data-testid="asset-type-select"
                      className="bg-navy-900 border-white/10 text-white h-12"
                    >
                      <SelectValue placeholder="Select asset type" />
                    </SelectTrigger>
                    <SelectContent className="bg-navy-800 border-white/10">
                      <SelectItem value="equipment" className="text-white hover:bg-navy-700">Equipment</SelectItem>
                      <SelectItem value="vehicle" className="text-white hover:bg-navy-700">Vehicle</SelectItem>
                      <SelectItem value="business-loan" className="text-white hover:bg-navy-700">Business Loan</SelectItem>
                      <SelectItem value="line-of-credit" className="text-white hover:bg-navy-700">Line of Credit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Loan Amount */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <Label className="font-body text-white/80">Loan Amount</Label>
                    <span className="font-mono text-gold-500 text-lg">{formatCurrency(loanAmount)}</span>
                  </div>
                  <Slider
                    data-testid="loan-amount-slider"
                    value={[loanAmount]}
                    onValueChange={(value) => setLoanAmount(value[0])}
                    min={10000}
                    max={2000000}
                    step={5000}
                    className="py-4"
                  />
                  <div className="flex justify-between text-white/40 font-mono text-xs">
                    <span>$10,000</span>
                    <span>$2,000,000</span>
                  </div>
                  <Input
                    data-testid="loan-amount-input"
                    type="number"
                    value={loanAmount}
                    onChange={(e) => setLoanAmount(Number(e.target.value))}
                    className="bg-navy-900 border-white/10 text-white font-mono h-12"
                    min={10000}
                    max={2000000}
                  />
                </div>

                {/* Interest Rate */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <Label className="font-body text-white/80">Interest Rate (p.a.)</Label>
                    <span className="font-mono text-gold-500 text-lg">{interestRate}%</span>
                  </div>
                  <Slider
                    data-testid="interest-rate-slider"
                    value={[interestRate]}
                    onValueChange={(value) => setInterestRate(value[0])}
                    min={4}
                    max={20}
                    step={0.25}
                    className="py-4"
                  />
                  <div className="flex justify-between text-white/40 font-mono text-xs">
                    <span>4%</span>
                    <span>20%</span>
                  </div>
                </div>

                {/* Loan Term */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <Label className="font-body text-white/80">Loan Term</Label>
                    <span className="font-mono text-gold-500 text-lg">{loanTerm} months</span>
                  </div>
                  <Slider
                    data-testid="loan-term-slider"
                    value={[loanTerm]}
                    onValueChange={(value) => setLoanTerm(value[0])}
                    min={12}
                    max={84}
                    step={6}
                    className="py-4"
                  />
                  <div className="flex justify-between text-white/40 font-mono text-xs">
                    <span>12 months</span>
                    <span>84 months</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="space-y-6">
              {/* Main Result Card */}
              <div className="bg-navy-700 border border-gold-500/20 p-8 lg:p-10">
                <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight mb-8">
                  Your Estimate
                </h2>

                <div className="text-center mb-8">
                  <p className="font-body text-white/60 mb-2">Estimated Monthly Repayment</p>
                  <p 
                    data-testid="monthly-payment-result"
                    className="font-mono text-5xl lg:text-6xl font-bold text-gold-500"
                  >
                    {formatCurrencyFull(results.monthlyPayment)}
                  </p>
                </div>

                {/* Progress Ring Visual */}
                <div className="flex justify-center mb-8">
                  <div className="relative w-48 h-48">
                    <svg className="w-full h-full transform -rotate-90">
                      <circle
                        cx="96"
                        cy="96"
                        r="80"
                        stroke="#112240"
                        strokeWidth="16"
                        fill="none"
                      />
                      <circle
                        cx="96"
                        cy="96"
                        r="80"
                        stroke="#FDB931"
                        strokeWidth="16"
                        fill="none"
                        strokeDasharray={`${principalPercent * 5.02} 502`}
                        className="transition-all duration-500"
                      />
                      <circle
                        cx="96"
                        cy="96"
                        r="80"
                        stroke="#38BDF8"
                        strokeWidth="16"
                        fill="none"
                        strokeDasharray={`${interestPercent * 5.02} 502`}
                        strokeDashoffset={`${-principalPercent * 5.02}`}
                        className="transition-all duration-500"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <p className="font-mono text-2xl font-bold text-white">{loanTerm}</p>
                        <p className="font-body text-white/60 text-sm">months</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Legend */}
                <div className="flex justify-center gap-8">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-gold-500 rounded-sm"></div>
                    <span className="font-body text-white/60 text-sm">Principal</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-[#38BDF8] rounded-sm"></div>
                    <span className="font-body text-white/60 text-sm">Interest</span>
                  </div>
                </div>
              </div>

              {/* Summary */}
              <div className="bg-navy-700 border border-white/5 p-8">
                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-4 border-b border-white/10">
                    <span className="font-body text-white/60">Loan Amount</span>
                    <span className="font-mono text-lg text-white">{formatCurrency(loanAmount)}</span>
                  </div>
                  <div className="flex justify-between items-center pb-4 border-b border-white/10">
                    <span className="font-body text-white/60">Total Interest</span>
                    <span data-testid="total-interest-result" className="font-mono text-lg text-[#38BDF8]">{formatCurrencyFull(results.totalInterest)}</span>
                  </div>
                  <div className="flex justify-between items-center pt-2">
                    <span className="font-heading font-bold text-lg text-white uppercase">Total Repayment</span>
                    <span data-testid="total-payment-result" className="font-mono text-2xl text-gold-500">{formatCurrencyFull(results.totalPayment)}</span>
                  </div>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="flex items-start gap-3 p-4 bg-navy-900 border border-white/5 rounded-sm">
                <Info size={18} className="text-gold-500 mt-0.5 flex-shrink-0" />
                <div className="font-body text-white/50 text-xs space-y-2">
                  <p>
                    *This calculator provides estimates only. Your interest rate may be different and is based on your credit history, 
                    the information you provide, and our assessment of your application. Rates, repayments and fees are estimates 
                    and are subject to change at any time.
                  </p>
                  <p>
                    *Comparison rate based on $30,000 secured loan over 5 years. <span className="text-white/70 font-medium">WARNING:</span> Different 
                    amounts and terms will result in different comparison rates. Eligibility criteria, terms and conditions apply.
                  </p>
                </div>
              </div>

              {/* CTA */}
              <Link to="/contact">
                <Button 
                  data-testid="calculator-apply-btn"
                  className="w-full gold-gradient text-black font-heading font-bold text-lg uppercase tracking-wider py-6 hover:scale-[1.02] transition-transform gold-glow"
                >
                  Apply Now
                  <ArrowRight size={20} className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Calculator;
